import { toast } from 'sonner'

/**
 * Check if the user prefers reduced motion.
 * When true, toasts should dismiss instantly (duration=0) per AC5.
 */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/**
 * Resolve toast duration: 0 when reduced motion is preferred, otherwise the provided or default value.
 */
function resolveDuration(custom?: number): number {
  if (prefersReducedMotion()) return 0
  return custom ?? 3000
}

/**
 * Show a silent side-effect toast notification.
 *
 * IMPORTANT: Toasts are NEVER used for errors.
 * Errors must use inline messages (field validation) or dialogs (destructive confirmations).
 * Toast is for non-blocking success feedback only.
 */
export function showToast(message: string, opts?: { duration?: number }): void {
  toast(message, {
    duration: resolveDuration(opts?.duration),
  })
}

/**
 * Show a toast notification with an Undo action for reversible operations.
 * The undo callback is available for the full toast duration (3 seconds by default).
 *
 * @param message - The toast message to display
 * @param undoLabel - Label for the undo button (use t('shell.toast.undo'))
 * @param onUndo - Callback invoked when the user taps Undo
 */
export function showUndoToast(
  message: string,
  undoLabel: string,
  onUndo: () => void,
  opts?: { duration?: number },
): void {
  toast(message, {
    duration: resolveDuration(opts?.duration),
    action: {
      label: undoLabel,
      onClick: onUndo,
    },
  })
}
